import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, BarChart3, Loader2, Zap } from "lucide-react";
import { toast } from "sonner";

import CarregandoRelatorio from "../relatorios-ofertas/comum/Carregando";

import { CarregandoBloco, CarregandoFormulario } from "../../components/carregando";
import { Button } from "../../components/ui/button";

import { ROTA_CAMPANHAS } from "../ofertas-do-dia/admin/rotas";
import { carregarConfig, salvarConfig } from "./api";
import CartaoLigado from "./config/CartaoLigado";
import ComoFunciona from "./config/ComoFunciona";
import PreviaCleo from "./config/PreviaCleo";
import SecaoExemplos from "./config/SecaoExemplos";
import SecaoTom from "./config/SecaoTom";
import { ROTA_RELATORIO_RECUPERADOR } from "./rotas";

// Impulsione → Campanhas → Recuperador de Vendas (antes: popup "Mensagens").
export default function ConfigRecuperadorPage() {
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [ativa, setAtiva] = useState(false);
  const [tom, setTom] = useState("");
  const [exemplos, setExemplos] = useState([]);
  const [alterado, setAlterado] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const aplicar = (d) => { setDados(d); setAtiva(!!d.is_active); setTom(d.tom || ""); setExemplos(d.exemplos?.length ? d.exemplos : [""]); setAlterado(false); };

  // Erro ao ler a configuração: aviso com "Tentar de novo" em vez de piscar para sempre (25/10/2026)
  const [erro, setErro] = useState(false);
  const [tentativa, setTentativa] = useState(0);
  useEffect(() => {
    setErro(false);
    carregarConfig().then(aplicar).catch(() => { setErro(true); toast.error("Não foi possível carregar o Recuperador de Vendas"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tentativa]);

  const mudar = (setter) => (valor) => { setter(valor); setAlterado(true); };

  const salvar = async () => {
    setSalvando(true);
    try {
      aplicar(await salvarConfig({ is_active: ativa, tom, exemplos: exemplos.filter((e) => e.trim()) }));
      toast.success(ativa ? "Recuperador salvo e ligado" : "Recuperador salvo");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao salvar");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-3xl mx-auto pb-6 space-y-4" data-e2e="config-recuperador">
        <button type="button" onClick={() => navigate(ROTA_CAMPANHAS)}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" /> Campanhas
        </button>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                Recuperador de Vendas
                {dados && (
                  <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${ativa ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-muted text-muted-foreground"}`}>
                    {ativa ? "Ligado" : "Desligado"}
                  </span>
                )}
              </h1>
              <p className="text-sm text-muted-foreground">A CLEO chama de volta, no WhatsApp, quem conversou e não pediu.</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate(ROTA_RELATORIO_RECUPERADOR)}>
            <BarChart3 className="w-4 h-4 mr-1.5" /> Ver relatório
          </Button>
        </div>

        {!dados && erro ? <CarregandoRelatorio erro onTentar={() => setTentativa((t) => t + 1)} /> : !dados ? (
          // Carregamento com cards (25/10/2026): cartão liga/desliga, como funciona e o tom piscando.
          <>
            <CarregandoBloco altura="h-[88px]" />
            <CarregandoBloco altura="h-48" />
            <CarregandoFormulario campos={2} />
          </>
        ) : (
          <>
            <CartaoLigado ativa={ativa} onAtiva={mudar(setAtiva)} />
            <ComoFunciona regras={dados.regras} />
            <SecaoTom tom={tom} onMudar={mudar(setTom)} />
            <SecaoExemplos exemplos={exemplos} onMudar={mudar(setExemplos)} />
            <PreviaCleo cenarios={dados.cenarios} tom={tom} exemplos={exemplos} />
          </>
        )}
      </div>

      {dados && (
        // Barra presa no pé da área de conteúdo (sticky), não da janela: com "fixed" o
        // menu lateral cobria o começo dela em telas menores.
        <div className="sticky bottom-0 z-20 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 border-t bg-card/95 backdrop-blur">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {alterado ? <b className="text-amber-600 dark:text-amber-400">Alterações não salvas</b> : "Tudo salvo."}
            </p>
            <Button onClick={salvar} disabled={salvando} data-e2e="salvar-recuperador">
              {salvando && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Salvar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
